import { api } from './api';

export interface FlowStation {
  _id: string;
  name: string;
  slug?: string;
  location: {
    _id: string;
    name: string;
  } | string;
  capacityRating: number;
  commissioningDate: string;
  status: 'Operational' | 'Under Maintenance' | 'Offline';
  owner: string;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export interface CreateFlowStationData {
  name: string;
  location: string;
  capacityRating: number | string;
  commissioningDate: string;
  status: 'Operational' | 'Under Maintenance' | 'Offline';
  owner: string;
}

export interface UpdateFlowStationData extends Partial<CreateFlowStationData> {}

export interface FlowStationResponse {
  message: string;
  data: {
    result: FlowStation[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: null | number;
    nextPage: null | number;
  };
  error: boolean;
}

export interface SingleFlowStationResponse {
  message: string;
  data: FlowStation;
  error: boolean;
}

type FlowStationMutationPayload = Omit<CreateFlowStationData, 'capacityRating'> & {
  capacityRating: string;
};

const normalizeFlowStationPayload = (
  data: CreateFlowStationData | UpdateFlowStationData
): FlowStationMutationPayload | UpdateFlowStationData => {
  if (data.capacityRating === undefined) {
    return data;
  }

  return {
    ...data,
    capacityRating: String(data.capacityRating),
  };
};

const flowStationService = {
  // Create a new flow station
  createFlowStation: async (data: CreateFlowStationData): Promise<FlowStation> => {
    const response = await api.post('/flow-station', normalizeFlowStationPayload(data));
    return response.data.data || response.data.result || response.data;
  },

  // Get all flow stations
  getAllFlowStations: async (): Promise<FlowStation[]> => {
    const response = await api.get('/flow-station');
    // Handle the nested response structure
    if (response.data.data && response.data.data.result) {
      return response.data.data.result;
    }
    return response.data.data || response.data.result || response.data || [];
  },

  // Get flow station by ID
  getFlowStationById: async (id: string): Promise<FlowStation> => {
    const response = await api.get(`/flow-station/${id}`);
    return response.data.data || response.data.result || response.data;
  },

  // Update flow station
  updateFlowStation: async (id: string, data: UpdateFlowStationData): Promise<FlowStation> => {
    const response = await api.put(`/flow-station/${id}`, normalizeFlowStationPayload(data));
    return response.data.data || response.data.result || response.data;
  },

  // Delete flow station
  deleteFlowStation: async (id: string): Promise<void> => {
    await api.delete(`/flow-station/${id}`);
  },

  // Get flow stations by location (field)
  getFlowStationsByLocation: async (locationId: string): Promise<FlowStation[]> => {
    const response = await api.get(`/flow-station/location/${locationId}`);
    const data = response.data.data;
    if (data && data.result) return data.result;
    return Array.isArray(data) ? data : [];
  },

  // Get flow stations by owner (client) - filters on frontend
  getFlowStationsByOwner: async (ownerId: string): Promise<FlowStation[]> => {
    const response = await api.get('/flow-station');
    
    // Handle the nested response structure
    let allFlowStations: FlowStation[] = [];
    if (response.data.data && response.data.data.result) {
      allFlowStations = response.data.data.result;
    } else {
      allFlowStations = response.data.data || response.data.result || response.data || [];
    }
    
    // Filter by owner ID on the frontend
    return allFlowStations.filter(flowStation => flowStation.owner === ownerId);
  }
};

export default flowStationService;
